<template>
    <div class="estate-description">
        <div class="row">
            <div class="col-12 text-center">
                <div class="d-flex align-items-center justify-content-between gap-3 text-start">
                    <div>{{ $t('form.leadingParagraph') }}</div>
                    <!-- Actions of the whole form, right above the end of the segments -->
                    <slot name="actions"/>
                </div>

                <div class="segment-btn-group mt-2 mb-4">
                    <template v-for="(segment, segmentIndex) in segmentConfigurations" :key="segmentIndex">
                    <router-link v-if="accessibleSegments.includes(segmentIndex)"
                                 :to="segment.path">
                        {{ segment.label }}
                    </router-link>
                    <div v-else>{{ segment.label }}</div>
                    </template>
                    <a v-if="lastKnownEstimateState.calculationUrl"
                       :href="lastKnownEstimateState.calculationUrl">{{ $t('form.segments.calculation') }}</a>
                    <div v-else>{{ $t('form.segments.calculation') }}</div>
                </div>
            </div>
            <div class="col-12">
                <div v-if="errorMessage !== null" class="alert alert-danger">{{ errorMessage }}</div>

                <div v-for="(segmentConfig, index) in segmentConfigurations" :key="segmentConfig.path" v-show="$route.path === segmentConfig.path">
                    <component :is="segmentConfig.component" v-bind="segmentConfig.props" :estateType="estimate.estateType" :ref="(el: any) => setSegmentRef(index, el)"/>
                </div>


                <div class="float-end">
                    <button v-if="loading" disabled="" class="btn btn-lg btn-outline-primary align-middle">
                        <span class="spinner-border spinner-border-sm me-2 align-middle" role="status"></span>
                        {{ $t('form.navigation.loading') }} ...
                    </button>

                    <template v-else>
                        <template v-if="showFinishButton && canCreateEstimate()">
                            <button v-if="lastKnownEstimateState.calculationUrl === null"
                                    class="btn btn-lg"
                                    :class="{'disabled': ! online, 'btn-primary': isLastSegment(), 'btn-outline-primary': !isLastSegment()}"
                                    :title="! online ? $t('form.errors.cannotCreateEstimateOffline') : null"
                                    @click="withSpinner(createEstimate())">{{ $t('form.navigation.createEstimate') }}</button>
                            <a v-else-if="isLastSegment()" :href="lastKnownEstimateState.calculationUrl"
                               class="btn btn-lg btn-primary">{{ $t('form.navigation.goToEstimate') }}</a>
                        </template>

                        <button v-if="! isLastSegment()" class="btn btn-lg btn-primary ms-2" @click="submitSegmentAndContinue">
                            {{ $t('form.navigation.continue') }}
                        </button>
                    </template>
                </div>

                <div v-if="currentSegment > 0 && segmentConfigurations[currentSegment - 1]">
                    <router-link :to="segmentConfigurations[currentSegment - 1].path">
                        {{ $t('form.navigation.previous') }} - {{ segmentConfigurations[currentSegment - 1].label }}
                    </router-link>
                </div>
            </div>
        </div>
        <SyncDialog :offline-manager="offlineManager"
                    :last-known-estimate-state="lastKnownEstimateState"
                    @sync="lastKnownEstimateState = $event"
                    ref="syncDialog"/>
        <NotSubmittedDataDialog ref="notSubmittedDataDialog"/>
    </div>
</template>

<script lang="ts">
import {Segment} from '@/composables/Segment';
import {EstimateEstateType, EstimateState} from '@/model/api';
import {BadResponseError} from '@/model/errors/BadResponseError';
import {ErrorWithUserMessage} from '@/model/errors/ErrorWithUserMessage';
import {NetworkError} from '@/model/errors/NetworkError';
import {Estimate} from '@/model/Estimate';
import {log} from '@/model/logger';
import {NotSubmittedParameters, OfflineManager} from '@/model/offline';
import {shouldNotHappen} from '@/utils';
import {isLeft} from 'fp-ts/lib/Either';
import {computed, defineComponent, inject, nextTick, onBeforeUnmount, onMounted, PropType, provide, ref} from 'vue';
import {isNavigationFailure, Router, useRouter} from 'vue-router';
import NotSubmittedDataDialog from './NotSubmittedDataDialog.vue';
import {SegmentConfiguration} from './segments';
import SyncDialog from './SyncDialog.vue';

export default defineComponent({
    components: {NotSubmittedDataDialog, SyncDialog},
    props: {
        estimate: {
            type: Object as PropType<EstimateState>,
            required: true,
        },
        segmentConfigurationsFactory: {
            type: Function as PropType<(estimate: EstimateState) => SegmentConfiguration[]>,
            required: true,
        },
        showFinishButton: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['save'],
    setup(props, {emit}) {
        const model = inject<Estimate<EstimateState>>('model')!;
        const notSubmittedParameters = inject<NotSubmittedParameters>('notSubmittedParameters')!;
        const offlineManager = inject<OfflineManager<EstimateState>>('offlineManager')!;
        const router = useRouter();

        const segmentRefs: Record<number, Segment<EstimateState>> = {};

        function setSegmentRef(index: number, el: any): void {
            if (el) {
                segmentRefs[index] = el;
            } else {
                delete segmentRefs[index];
            }
        }
        const syncDialog = ref<InstanceType<typeof SyncDialog> | null>(null);
        const notSubmittedDataDialog = ref<InstanceType<typeof NotSubmittedDataDialog> | null>(null);

        provide('estateType', props.estimate.estateType as EstimateEstateType);

        const errorMessage = ref<string | null>(null);
        const accessibleSegments = ref<number[]>([]);
        const currentSegment = ref(0);
        const loading = ref(false);
        const online = ref(true);
        const lastKnownEstimateState = ref<EstimateState>(props.estimate);

        const segmentConfigurations = computed(() => {
            return props.segmentConfigurationsFactory(lastKnownEstimateState.value);
        });

        function canCreateEstimate(): boolean {
            const segs = segmentRefs;
            return Object.entries(segs)
                .filter(([index]) => Number(index) > currentSegment.value)
                .every(([, segment]) => segment.allowsMovingOn());
        }

        function isLastSegment(): boolean {
            return currentSegment.value === segmentConfigurations.value.length - 1;
        }

        function submitSegmentAndContinue(): void {
            save()
                .then(success => {
                    if (!success) {
                        return;
                    }

                    return router.push(segmentConfigurations.value[currentSegment.value + 1].path);
                })
                .catch(error => {
                    if (isNavigationFailure(error)) {
                        return;
                    }

                    log(error);
                });
        }

        async function createEstimate(): Promise<void> {
            errorMessage.value = null;

            const segmentSubmitSuccessful = await submitCurrentSegment();

            if (!segmentSubmitSuccessful) {
                return;
            }

            const estimateUrlOrError = await model.createEstimate();

            if (isLeft(estimateUrlOrError)) {
                const error = estimateUrlOrError.left;

                if (error instanceof ErrorWithUserMessage) {
                    errorMessage.value = error.message;
                } else if (error instanceof NetworkError) {
                    pollServerUntilUserIsOnline();
                } else if (error instanceof BadResponseError) {
                    log(`Got BadResponseError (${error.code} ${error.message}) when trying to create estimate. This is a bug!`);
                } else {
                    shouldNotHappen(error);
                }

                return;
            }

            window.location.href = estimateUrlOrError.right;
        }

        function initializeRouter(): void {
            const segmentPaths = segmentConfigurations.value.map(({path}) => ({path}));
            const lastAvailableSegment = accessibleSegments.value.includes(1) ? 1 : 0;
            const updateCurrentSegment = () => {
                updateAccessibleSegments();
                currentSegment.value = segmentPaths.findIndex(s => s.path === router.currentRoute.value.path);
                nextTick(() => {
                    const segs = segmentRefs;
                    if (segs && segs[currentSegment.value]) {
                        segs[currentSegment.value].beforeEnter();
                    }
                });
                errorMessage.value = null;
            };

            const emptyComponent = defineComponent({render: () => null});
            for (const segmentPath of segmentPaths) {
                router.addRoute({...segmentPath, component: emptyComponent});
            }
            router.addRoute({path: '/:catchAll(.*)', redirect: () => segmentPaths[lastAvailableSegment].path});

            router.isReady().then(async () => {
                await router.replace(router.currentRoute.value.fullPath);
                updateCurrentSegment();

                if (currentSegment.value < 0 || !accessibleSegments.value.includes(currentSegment.value)) {
                    await router.replace(segmentPaths[lastAvailableSegment].path);
                    updateCurrentSegment();
                }
            });

            router.beforeEach(async () => {
                const segs = segmentRefs;
                const seg = segs ? segs[currentSegment.value] : null;

                if (seg === null || seg === undefined || !seg.hasNotSubmittedChanges()) {
                    return true;
                }

                const userWantsToSaveSegment = await notSubmittedDataDialog.value!.show();

                if (userWantsToSaveSegment) {
                    const success = await save();
                    return success ? true : false;
                }

                seg.clearNotSubmittedChanges();
                return true;
            });

            router.afterEach(updateCurrentSegment);
        }

        function save(): Promise<boolean> {
            return withSpinner(submitCurrentSegment());
        }

        async function submitCurrentSegment(): Promise<boolean> {
            const seg = segmentRefs[currentSegment.value];

            if (!seg) {
                return false;
            }

            const result = await seg.submit();

            if (isLeft(result)) {
                const error = result.left;

                if (error instanceof NetworkError) {
                    pollServerUntilUserIsOnline();
                    return false;
                }

                if (error === null) {
                    const firstError = (seg as any).$el.querySelector('.invalid-feedback, .is-invalid');

                    if (firstError !== null) {
                        firstError.scrollIntoView({behavior: 'smooth', block: 'center'});
                    }

                    log('Segment submit has failed');
                    return false;
                }

                return shouldNotHappen(error);
            }

            const estimate = result.right;

            if (estimate !== null) {
                emit('save', currentSegment.value, estimate, lastKnownEstimateState.value);
                lastKnownEstimateState.value = estimate;
            }

            return true;
        }

        /**
         * Shows the estimate saved from outside of the segments, the shown segment takes its values again,
         * with its values not saved yet over them
         */
        function applyEstimate(estimate: EstimateState): void {
            lastKnownEstimateState.value = estimate;

            nextTick(() => {
                segmentRefs[currentSegment.value]?.beforeEnter();
                updateAccessibleSegments();
            });
        }

        /**
         * Goes to the segment of the path, to the last accessible one when the user cannot get to it yet
         */
        function showSegment(path: string): void {
            const index = segmentConfigurations.value.findIndex(segment => segment.path === path);
            const accessible = accessibleSegments.value.includes(index)
                ? index
                : Math.max(...accessibleSegments.value.filter(current => current < index), 0);

            void router.push(segmentConfigurations.value[accessible].path);
        }

        function pollServerUntilUserIsOnline(): void {
            online.value = false;
            offlineManager.waitForConnection()
                .then(estimate => {
                    if (offlineManager.isInSyncWith(estimate)) {
                        return;
                    }

                    lastKnownEstimateState.value = estimate;
                    syncDialog.value!.show();
                    online.value = true;
                });
        }

        function onBeforeUnloadHandler(event: BeforeUnloadEvent): void {
            if (Object.keys(notSubmittedParameters.get()).length !== 0) {
                event.preventDefault();
                event.returnValue = '';
            }
        }

        function onUnloadHandler(): void {
            notSubmittedParameters.clear();
        }

        function registerEventsRelatedToNotSubmittedParameters(): void {
            window.addEventListener('beforeunload', onBeforeUnloadHandler);
            window.addEventListener('unload', onUnloadHandler);
        }

        onBeforeUnmount(() => {
            window.removeEventListener('beforeunload', onBeforeUnloadHandler);
            window.removeEventListener('unload', onUnloadHandler);
            notSubmittedParameters.clear();
        });

        function withSpinner<TResult>(promise: Promise<TResult>): Promise<TResult> {
            loading.value = true;

            return promise.finally(() => loading.value = false);
        }

        function updateAccessibleSegments(): void {
            const segs = segmentRefs;

            accessibleSegments.value = [];

            const segCount = segmentConfigurations.value.length;
            for (let key = 0; key < segCount; key++) {
                if (key === 0 || (accessibleSegments.value.includes(key - 1) && segs[key - 1]?.allowsMovingOn())) {
                    accessibleSegments.value.push(key);
                }
            }
        }

        onMounted(() => {
            if (!offlineManager.isInSyncWith(lastKnownEstimateState.value)) {
                syncDialog.value!.show();
            }

            updateAccessibleSegments();
            initializeRouter();
            registerEventsRelatedToNotSubmittedParameters();
        });

        return {
            setSegmentRef,
            syncDialog,
            notSubmittedDataDialog,
            segmentConfigurations,
            errorMessage,
            accessibleSegments,
            currentSegment,
            loading,
            online,
            lastKnownEstimateState,
            offlineManager,
            canCreateEstimate,
            isLastSegment,
            submitSegmentAndContinue,
            createEstimate,
            withSpinner,
            applyEstimate,
            showSegment,
        };
    },
});
</script>
